import { useState } from 'react'
import { WarehouseView } from '@/console/api/types'
import { Button } from '@/console/ui/Button'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Field } from '@/console/ui/Field'
import { Tag } from '@/console/ui/Tag'
import { PolicyCard, SaveErrorCallout } from './PolicyCard'
import { outboundPrefixFormSchema, savedPrefix, toOutboundPrefixPolicy, validationMessages } from './policyForm'
import { usePolicySave, WriteCallbacks } from './usePolicySave'
import styles from './Warehouse.module.css'

const LockIcon = (): JSX.Element => (
  <svg className={styles.lockIcon} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
)

/**
 * LOGISPRD-15602 — OUTBOUND_SEQUENCE_PREFIX. 한 번 저장하면 바꿀 수 없다.
 * 서버에 값이 있으면 값만 크게 보이고 입력·버튼을 두지 않는다. 값이 없을 때만 입력 가능하며 저장 전 확인창(danger)을 거친다.
 * 형식·센터 코드·중복 검증은 BE 가 한다 — 실패 문구를 그대로 카드에 보여준다.
 */
export const OutboundPrefixCard = ({
  view,
  warehouseCode,
  ...callbacks
}: WriteCallbacks & { view: WarehouseView; warehouseCode: string }): JSX.Element => {
  const saved = savedPrefix(view)
  const [prefix, setPrefix] = useState('')
  const [confirming, setConfirming] = useState(false)
  const { saving, error, save } = usePolicySave(warehouseCode, callbacks)

  if (saved !== null) {
    return (
      <PolicyCard title="출고번호 접두" typeName="OUTBOUND_SEQUENCE_PREFIX">
        <div className={styles.prefixLocked}>
          <span className={styles.prefixValue}>{saved}</span>
          <span className={styles.prefixLockNote}>
            <LockIcon />
            저장 후 변경할 수 없어요
          </span>
        </div>
      </PolicyCard>
    )
  }

  const problems = validationMessages(outboundPrefixFormSchema, { prefix })

  const confirmSave = (): void => {
    setConfirming(false)
    // 다이얼로그에 포커스 트랩이 없어 열린 동안 입력이 바뀔 수 있다 — 확인 시점에 다시 검증한다.
    if (validationMessages(outboundPrefixFormSchema, { prefix }).length > 0) {
      return
    }
    save(toOutboundPrefixPolicy({ prefix }))
  }

  return (
    <PolicyCard
      title="출고번호 접두"
      typeName="OUTBOUND_SEQUENCE_PREFIX"
      tags={<Tag tone="warn">미설정</Tag>}
      error={error ? <SaveErrorCallout error={error} /> : null}
      footerNote="저장하면 바꿀 수 없어요."
      footer={
        <Button size="sm" disabled={saving || problems.length > 0} onClick={() => setConfirming(true)}>
          {saving ? '저장 중…' : '저장'}
        </Button>
      }
    >
      <div className={styles.prefixInput}>
        <Field label="접두">
          <input type="text" spellCheck={false} placeholder="예: IC" value={prefix} disabled={saving} onChange={(e) => setPrefix(e.target.value)} />
        </Field>
      </div>
      {prefix.length > 0 && problems.length > 0 ? <p className={styles.formIssue}>{problems.join(' ')}</p> : null}
      <p className={styles.help}>앞뒤 공백은 지우고 보내요. 형식·중복은 서버가 검사해요.</p>

      <ConfirmDialog
        open={confirming}
        title="출고번호 접두 저장"
        body={
          <>
            <p className={styles.dialogValue}>{prefix.trim()}</p>
            <p className={styles.dialogLead}>
              <b>{warehouseCode}</b> 의 출고번호 접두로 저장할까요? 저장 후에는 변경할 수 없어요.
            </p>
          </>
        }
        confirmLabel="저장"
        danger
        onConfirm={confirmSave}
        onCancel={() => setConfirming(false)}
      />
    </PolicyCard>
  )
}
